import { useLiveQuery } from 'dexie-react-hooks';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newId, PROTOCOL_TARGETS, type HealthProtocolRecord, type ProtocolTarget, type ProtocolType } from '@dairy/shared';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Checkbox, Dialog, EmptyState, Field, Input, PageHeader, Segmented, Select } from '@/components/ui';
import { IntInput } from '@/components/inputs';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { save } from '@/db/repo';
import { reconcileAllReminders } from '@/features/events/effects';

/** HE-4 health protocols (owner). No protocols are pre-loaded. */
export function ProtocolsPage() {
  const { t } = useTranslation();
  const protocols = useLiveQuery(() => db.healthProtocols.toArray(), []) ?? [];
  const [editing, setEditing] = useState<HealthProtocolRecord | 'new' | null>(null);
  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader
        title={t('settings.protocols')}
        actions={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> {t('common.add')}
          </Button>
        }
      />
      <p className="rounded-xl bg-sky-50 p-3 text-sky-950">{t('protocols.help')}</p>
      {protocols.length === 0 ? <EmptyState>{t('protocols.empty')}</EmptyState> : null}
      <ul className="space-y-2">
        {protocols.map((p) => (
          <li key={p.id}>
            <Card className="p-0">
              <button className="flex min-h-16 w-full items-center gap-3 px-4 text-left" onClick={() => setEditing(p)}>
                <div className="flex-1">
                  <div className="font-semibold">
                    {p.name} {!p.active ? <Badge>{t('common.inactive')}</Badge> : null}
                  </div>
                  <div className="text-sm text-stone-600">
                    {t(`events.type.${p.type}`)} · {t('protocols.every', { count: p.intervalDays })} · {t(`protocols.target.${p.target}`)}
                  </div>
                </div>
              </button>
            </Card>
          </li>
        ))}
      </ul>
      {editing ? <ProtocolDialog item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function ProtocolDialog({ item, onClose }: { item: HealthProtocolRecord | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [type, setType] = useState<ProtocolType>(item?.type ?? 'VACCINATION');
  const [name, setName] = useState(item?.name ?? '');
  const [product, setProduct] = useState(item?.product ?? '');
  const [intervalDays, setIntervalDays] = useState(item ? String(item.intervalDays) : '');
  const [target, setTarget] = useState<ProtocolTarget>(item?.target ?? 'ALL');
  const [firstAge, setFirstAge] = useState(item?.firstDueAgeDays != null ? String(item.firstDueAgeDays) : '');
  const [active, setActive] = useState(item?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const n = Number(intervalDays);
    const fa = firstAge ? Number(firstAge) : null;
    if (!name.trim()) return setError(t('protocols.errors.name'));
    if (!Number.isInteger(n) || n < 1 || n > 730) return setError(t('protocols.errors.interval'));
    if (fa !== null && (!Number.isInteger(fa) || fa < 0 || fa > 3650)) return setError(t('protocols.errors.firstAge'));
    try {
      await save('healthProtocol', { ...(item ?? {}), id: item?.id ?? newId(), type, name: name.trim(), product: product.trim() || null, intervalDays: n, target, firstDueAgeDays: fa, active });
      await reconcileAllReminders();
      toast(t('common.saved'));
      onClose();
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={item ? t('protocols.edit') : t('protocols.add')} footer={<Button onClick={() => void submit()}>{t('common.save')}</Button>}>
      <div className="space-y-3">
        <Segmented value={type} onChange={setType} options={[{ value: 'VACCINATION', label: t('events.type.VACCINATION') }, { value: 'DEWORMING', label: t('events.type.DEWORMING') }]} />
        <Field label={t('protocols.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </Field>
        <Field label={t('events.product')}>
          <Input value={product} onChange={(e) => setProduct(e.target.value)} maxLength={80} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('protocols.interval')}>
            <IntInput value={intervalDays} onChange={(e) => setIntervalDays(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label={t('protocols.firstAge')}>
            <IntInput value={firstAge} onChange={(e) => setFirstAge(e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>
        <Field label={t('protocols.targetLabel')}>
          <Select value={target} onChange={(e) => setTarget(e.target.value as ProtocolTarget)}>
            {PROTOCOL_TARGETS.map((x) => (
              <option key={x} value={x}>
                {t(`protocols.target.${x}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox label={t('common.active')} checked={active} onChange={(e) => setActive(e.target.checked)} />
        {error ? <p className="text-red-700">{error}</p> : null}
      </div>
    </Dialog>
  );
}
